import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { sectionTemplates } from "../../../data/database";
import { normalizeStyle } from "../utils/sectionStyle";
import { pagesApi } from "../../../api/pagesApi";

import {
  XMarkIcon,
  PaintBrushIcon,
  DocumentTextIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";

import ConfirmDeleteModal from "../../../components/GeneralComponents/ConfirmDeleteModal";

import { useLangDict } from "./sectioneditormodal/useLangDict";
import ContentTab from "./sectioneditormodal/ContentTab";
import DesignTab from "./sectioneditormodal/DesignTab";

function resolveSectionTypeKey(rawType, templatesObj) {
  const t = (rawType || "").trim();
  if (!t) return t;

  const candidates = [
    t,
    t.toLowerCase(),
    t === "categorySection" ? "categorysection" : null,
    t === "categorysection" ? "categorySection" : null,
    t === "locationSection" ? "locationsection" : null,
    t === "locationsection" ? "locationSection" : null,
    t === "serviceSection" ? "servicesection" : null,
    t === "servicesection" ? "serviceSection" : null,
  ].filter(Boolean);

  return candidates.find((k) => templatesObj?.[k]) || t;
}

function getSectionIdentity(section) {
  return (
    section?.id ||
    section?._id ||
    section?.sectionId ||
    `${section?.type || ""}__${section?.template || ""}`
  );
}

function useDebouncedCallback(callback, delay = 120) {
  const timeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return useCallback(
    (...args) => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        callback?.(...args);
      }, delay);
    },
    [callback, delay]
  );
}

export default function SectionEditorModal({ section, pageId, onSave, onClose, onPatch }) {
  console.log("SECTION MODAL PROPS:", {
    pageId,
    section,
  });
  const { t, ready } = useLangDict();

  const [editedSection, setEditedSection] = useState(() => ({
    ...section,
    style: normalizeStyle(section?.style),
  }));

  const [activeTab, setActiveTab] = useState("content");
  const [isSaving, setIsSaving] = useState(false);
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);


  const sectionIdentity = useMemo(() => getSectionIdentity(section), [section]);
  const lastSectionIdentityRef = useRef(sectionIdentity);

  const debouncedPatch = useDebouncedCallback(onPatch, 120);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    if (lastSectionIdentityRef.current !== sectionIdentity) {
      setEditedSection({
        ...section,
        style: normalizeStyle(section?.style),
      });
      lastSectionIdentityRef.current = sectionIdentity;
    }
  }, [section, sectionIdentity]);

  const resolvedType = useMemo(
    () => resolveSectionTypeKey(editedSection?.type, sectionTemplates),
    [editedSection?.type]
  );

  const templateData = useMemo(() => {
    return sectionTemplates?.[resolvedType]?.[editedSection?.template];
  }, [resolvedType, editedSection?.template]);

  const tplCfg = templateData?.config || {};
  const cfg = editedSection?.config || {};

  const mergedCfg = useMemo(
    () => ({ ...tplCfg, ...(editedSection?.config || {}) }),
    [tplCfg, editedSection?.config]
  );

  const hasKey = useCallback(
    (key) => Object.prototype.hasOwnProperty.call(cfg, key),
    [cfg]
  );
  const handleUploadImage = useCallback(
    (file, opts = {}) => {
      if (!pageId) {
        throw new Error("Missing pageId for upload");
      }

      return pagesApi.uploadImage(file, {
        ...opts,
        pageId,
      });
    },
    [pageId]
  );
  const onStyleChange = useCallback((field, value) => {
    setEditedSection((prev) => ({
      ...prev,
      style: { ...(prev.style || {}), [field]: value },
    }));
    debouncedPatch?.({ style: { [field]: value } });
  }, [debouncedPatch]);

  const onConfigChange = useCallback((field, value) => {
    setEditedSection((prev) => ({
      ...prev,
      config: { ...(prev.config || {}), [field]: value },
    }));
    debouncedPatch?.({ config: { [field]: value } });
  }, [debouncedPatch]);

  const handleSubmit = async () => {
    setIsSaving(true);
    try {
      await onSave({
        ...editedSection,
        style: normalizeStyle(editedSection?.style),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const revertToSaved = () => {
    const next = { ...section, style: normalizeStyle(section?.style) };
    setEditedSection(next);
    onPatch?.({
      config: next.config || {},
      style: next.style || {},
    });
  };

  const resetToTemplateDefaults = () => {
    const nextCfg = { ...(templateData?.config || {}) };
    setEditedSection((prev) => ({ ...prev, config: nextCfg }));
    onPatch?.({ config: nextCfg });
  };


  // flags used by DesignTab
  const typeLower = (resolvedType || "").toLowerCase();
  const flags = {
    isHero: resolvedType === "hero",
    isGallery: resolvedType === "gallery",
    isCategorySection: typeLower === "categorysection",
    isFeatures: editedSection?.type === "features",
    isTestimonials: editedSection?.type === "testimonials",
    isCta: editedSection?.type === "cta",
    isLocationSection: typeLower === "locationsection",
    isServiceSection: typeLower === "servicesection",
  };

  // alignment defaults (same behavior you had)
  const featuresTplKey = useMemo(() => {
    const raw = String(editedSection?.template ?? "").toLowerCase().trim();
    if (raw === "3" || raw === "template3" || raw.endsWith("template3")) return "template3";
    if (raw === "2" || raw === "template2" || raw.endsWith("template2")) return "template2";
    return "template1";
  }, [editedSection?.template]);

  const locationTplKey = useMemo(() => {
    const raw = String(editedSection?.template ?? "").toLowerCase().trim();
    if (raw === "3" || raw === "template3" || raw.endsWith("template3")) return "template3";
    if (raw === "2" || raw === "template2" || raw.endsWith("template2")) return "template2";
    return "template1";
  }, [editedSection?.template]);

  const defaultLocationAlign =
    locationTplKey === "template1"
      ? (editedSection?.config?.direction === "rtl" ? "right" : "left")
      : "center";

  const defaultFeaturesAlign =
    featuresTplKey === "template3"
      ? (editedSection?.config?.direction === "rtl" ? "right" : "left")
      : "center";

  const defaultTextAlign =
    flags.isFeatures
      ? defaultFeaturesAlign
      : flags.isTestimonials
        ? "center"
        : flags.isLocationSection
          ? defaultLocationAlign
          : flags.isServiceSection
            ? "center"
            : "center";

  const defaultHeroAlign = editedSection?.config?.direction === "rtl" ? "right" : "left";

  const alignmentValue = flags.isHero
    ? (editedSection?.config?.alignment ||
      editedSection?.style?.textAlign ||
      editedSection?.style?.alignment ||
      defaultHeroAlign)
    : (editedSection?.style?.textAlign ||
      editedSection?.style?.alignment ||
      editedSection?.config?.alignment ||
      defaultTextAlign);

  const setAlignment = (v) => {
    // keep modern and legacy keys
    if (!flags.isHero) {
      onStyleChange("textAlign", v);
      onStyleChange("alignment", v);
    }
    if (flags.isGallery || flags.isCategorySection) onStyleChange("alignment", v);
    if (flags.isLocationSection || flags.isServiceSection || flags.isHero) onConfigChange("alignment", v);
  };

  const tabs = [
    {
      id: "content",
      label: t("sectionModal.tabs.content", "Content"),
      icon: <DocumentTextIcon className="w-4 h-4" />,
    },
    {
      id: "design",
      label: t("sectionModal.tabs.design", "Design"),
      icon: <PaintBrushIcon className="w-4 h-4" />,
    },
  ];
  if (!ready) {
    return (
      <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-gray-200 p-6">
          <div className="text-lg font-bold text-gray-900">Loading…</div>
          <div className="text-sm text-gray-500 mt-1">Preparing language</div>
        </div>
      </div>
    );
  }
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm overflow-y-auto">
      <div className="min-h-full flex items-stretch sm:items-start justify-center sm:p-4 sm:py-8">
        <div className="bg-white w-full min-h-screen sm:min-h-0 sm:max-w-3xl sm:max-h-[90vh] sm:rounded-2xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col">
          {/* Header */}
          <div className="shrink-0 px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="w-3 h-8 rounded-full bg-gradient-to-b from-blue-500 to-purple-600 mt-1" />
                <div>
                  <h2 className="text-lg font-bold text-gray-900">
                    {t("sectionModal.header.title", "Edit Section")}: {resolvedType}
                  </h2>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {t("sectionModal.header.template", "Template")}:{" "}
                    {templateData?.name || section.template}
                  </p>
                </div>
              </div>

              <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors group">
                <XMarkIcon className="w-6 h-6 text-gray-400 group-hover:text-gray-600 transition-colors" />
              </button>
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <div className="inline-flex gap-1 p-1 bg-gray-100 rounded-xl">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-all ${activeTab === tab.id
                      ? "bg-white text-gray-900 shadow-sm ring-1 ring-gray-200"
                      : "text-gray-600 hover:text-gray-900 hover:bg-white/50"
                      }`}
                  >
                    {tab.icon}
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-2">
                {activeTab === "content" ? (
                  <button
                    type="button"
                    onClick={() => setConfirmResetOpen(true)}
                    className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 bg-white border border-gray-300 rounded-lg"
                  >
                    {t("sectionModal.actions.resetDefaults", "Reset to defaults")}
                  </button>
                ) : null}
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 py-5 bg-white">
            {activeTab === "content" ? (
              <ContentTab
                resolvedType={resolvedType}
                editedSection={editedSection}
                cfg={cfg}
                mergedCfg={mergedCfg}
                hasKey={hasKey}
                onConfigChange={onConfigChange}
                uploadImage={handleUploadImage}
                t={t}
              />
            ) : (
              <DesignTab
                editedSection={editedSection}
                t={t}
                flags={flags}
                alignmentValue={alignmentValue}
                setAlignment={setAlignment}
                onStyleChange={onStyleChange}
              />
            )}

            <div className="text-xs text-gray-500 mt-6">
              {t("sectionModal.footer.previewNote", "Changes apply immediately to preview")}
            </div>
          </div>

          {/* Footer */}
          <div className="shrink-0 px-6 py-4 border-t border-gray-200 bg-gray-50/60">
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2.5 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-white border border-gray-300 rounded-lg transition-colors"
              >
                {t("sectionModal.footer.cancel", "Cancel")}
              </button>

              <button
                onClick={handleSubmit}
                disabled={isSaving}
                className="px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 rounded-lg shadow-sm hover:shadow transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSaving ? (
                  <>
                    <ArrowPathIcon className="w-4 h-4 animate-spin" />
                    {t("sectionModal.footer.saving", "Saving...")}
                  </>
                ) : (
                  t("sectionModal.footer.save", "Save Changes")
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* NEW: Reset confirmation */}
      <ConfirmDeleteModal
        open={confirmResetOpen}
        t={t}
        title={t("sectionModal.resetDefaults.title", "Reset to defaults?")}
        message={t(
          "sectionModal.resetDefaults.message",
          "This will replace the current content fields with the template defaults. You can still close the modal without saving."
        )}
        confirmText={t("sectionModal.resetDefaults.confirm", "Reset")}
        cancelText={t("sectionModal.resetDefaults.cancel", "Cancel")}
        loading={false}
        onClose={() => setConfirmResetOpen(false)}
        onConfirm={() => {
          resetToTemplateDefaults();
          setConfirmResetOpen(false);
        }}
      />
    </div>
  );
}